import type { DomainSourceGroup } from "@crux/contracts";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetchDomainSourceSites, fetchOverview } from "../api/index.js";
import { queryKeys } from "../query-keys.js";

/** The Overview screen. */

interface OverviewRange {
  readonly from: string;
  readonly to: string;
  /** Absent means every engine. A wildcard string would reach the API. */
  readonly engine?: string;
}

/**
 * The query itself, apart from the hook, so the export can ask for the same
 * numbers from a click — under the same key, so a range already on screen is
 * not fetched twice.
 */
export function overviewQuery(
  workspaceId: string,
  reportId: string,
  range: OverviewRange,
) {
  return {
    // The engine is part of the key. Without it, switching engines returns the
    // previous engine's cached numbers under the new label — a stale answer
    // that looks like a fresh one.
    queryKey: [
      ...queryKeys.overview(workspaceId, reportId, range.from, range.to),
      range.engine ?? "all",
    ],
    queryFn: () => fetchOverview(workspaceId, reportId, range),
  };
}

export function useOverview(workspaceId: string, reportId: string, range: OverviewRange) {
  return useQuery(overviewQuery(workspaceId, reportId, range));
}

/**
 * Every site in one part of the domain-sources ring, a page at a time. Asked
 * only by the list a pressed part shows, so there is no request until then.
 */
export function useDomainSourceSites(
  workspaceId: string,
  reportId: string,
  range: { readonly from: string; readonly to: string; readonly engine?: string },
  group: DomainSourceGroup,
) {
  return useInfiniteQuery({
    queryKey: queryKeys.domainSourceSites(workspaceId, reportId, { ...range, group }),
    queryFn: ({ pageParam }) =>
      fetchDomainSourceSites(workspaceId, reportId, {
        ...range,
        group,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
  });
}
